import { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import CancelModal from '@/app/common/components/CancelModal';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { DUPLICATE_EXECUTION_MESSAGE } from '@/app/TrackerAi/utils/api-errors';
import { buildTrackerListBody } from '@/app/TrackerAi/api/queryParams';
import {
  useFetchProjectTopicListPageMutation,
  useGetProjectTopicListQuery
} from '@/app/TrackerAi/api/trackerEndpointsApi';
import { TopicsPageSkeleton } from '../components/common/Skeleton';
import { NoProject } from '../components/NoProject';
import { PageFilterBar } from '../components/filters/PageFilterBar';
import { TopicFiltersBar } from '../components/topics/TopicFiltersBar';
import { PromptRunProgress } from '../components/prompts/PromptRunProgress';
import { TopicPromptsPanel } from '../components/topics/TopicPromptsPanel';
import { TopicSummaryCards } from '../components/topics/TopicSummaryCards';
import { TopicsTable } from '../components/topics/TopicsTable';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useProject } from '../context/ProjectContext';
import type { TopicOverview, TopicsOverviewSummary } from '../types';
import { GRID_PAGE_SIZE, toGridFetchFunction } from '../utils/grid-fetch';
import { DEFAULT_TOPIC_FILTERS, topicTableFilters } from '../utils/topic-filters';
import { buildDateRangeQueryOptions } from '../utils/visibility-range';

const EMPTY_SUMMARY: TopicsOverviewSummary = {
  totalTopics: 0,
  totalPrompts: 0,
  totalExecutions: 0,
  averageVisibility: 0
};

export function TopicsPage() {
  return (
    <NoProject>
      <TopicsContent />
    </NoProject>
  );
}

function TopicsContent() {
  const {
    filtersReady,
    visibilityRange,
    customFrom,
    customTo,
    topicFilterId,
    setNewTopicOpen,
    modelFilterId,
    domainFilterId,
    projectId,
    error,
    setError,
    refresh
  } = useProject();
  const [filters, setFilters] = useState(DEFAULT_TOPIC_FILTERS);
  const [expandedTopicId, setExpandedTopicId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TopicOverview | null>(null);
  const [deleting, setDeleting] = useState(false);
  const debouncedSearch = useDebouncedValue(filters.search, 300);

  const listArgs = useMemo(
    () => ({
      projectId: projectId ?? '',
      ...buildTrackerListBody(
        {
          ...buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
            topicId: topicFilterId ?? undefined,
            providerIds: modelFilterId ? [modelFilterId] : undefined
          }),
          domainFilter: domainFilterId
        },
        topicTableFilters({ ...filters, search: debouncedSearch })
      )
    }),
    [
      projectId,
      visibilityRange,
      customFrom,
      customTo,
      topicFilterId,
      modelFilterId,
      domainFilterId,
      filters,
      debouncedSearch
    ]
  );
  const {
    data,
    currentData,
    error: listError,
    isError: isListError,
    isFetching,
    refetch
  } = useGetProjectTopicListQuery(
    { ...listArgs, page: 1, perPage: GRID_PAGE_SIZE },
    { skip: !projectId || !filtersReady }
  );
  const [fetchTopicsPage] = useFetchProjectTopicListPageMutation();
  const fetchFunction = useMemo(
    () => toGridFetchFunction(fetchTopicsPage),
    [fetchTopicsPage]
  );
  const summary = data?.summary ?? EMPTY_SUMMARY;

  // Panel actions (run/delete prompt) do not refetch the table, so its open detail row
  // survives; pick up the new counts once the panel closes.
  const changeExpandedTopic = useCallback(
    (next: string | null) => {
      if (expandedTopicId && next !== expandedTopicId) void refetch();
      setExpandedTopicId(next);
    },
    [expandedTopicId, refetch]
  );
  const toggleTopic = useCallback(
    (topicId: string) => changeExpandedTopic(expandedTopicId === topicId ? null : topicId),
    [changeExpandedTopic, expandedTopicId]
  );

  async function confirmDelete() {
    if (!projectId || !pendingDelete) return;
    setDeleting(true);
    setError('');
    try {
      await api.deleteTopics(projectId, [pendingDelete.id]);
      if (expandedTopicId === pendingDelete.id) setExpandedTopicId(null);
      await Promise.all([refresh(), refetch()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  }

  const newTopicButton = (
    <Button
      variant="falcon-primary"
      size="sm"
      onClick={() => setNewTopicOpen(true)}
      className="flex-shrink-0"
    >
      + تاپیک جدید
    </Button>
  );

  if (shouldShowDataLoadError(isListError, listError)) {
    return (
      <div>
        <div className="mb-4">
          <PageFilterBar
            showModels={false}
            showTopics={false}
            showDomains={false}
            splitDateAndFilters
            actions={newTopicButton}
          />
        </div>
        <DataLoadError
          error={listError}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          isNetworkError={isNetworkApiError(listError)}
        />
      </div>
    );
  }

  if (!data || (isFetching && summary.totalTopics === 0)) {
    return (
      <div>
        <div className="mb-4">
          <PageFilterBar
            showModels={false}
            showTopics={false}
            showDomains={false}
            splitDateAndFilters
            actions={newTopicButton}
          />
        </div>
        <TopicsPageSkeleton />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <PageFilterBar
          showModels={false}
          showTopics={false}
          showDomains={false}
          splitDateAndFilters
          actions={newTopicButton}
        />
      </div>

      {error && error !== DUPLICATE_EXECUTION_MESSAGE ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}

      <div className="mb-4">
        <TopicSummaryCards
          summary={summary}
          domainFilterActive={domainFilterId !== 'all'}
        />
      </div>

      <TopicFiltersBar
        filters={filters}
        onChange={setFilters}
      />

      <PromptRunProgress onComplete={() => void refetch()} />

      {summary.totalTopics === 0 ? (
        <Card className="border-dashed">
          <Card.Body className="py-5 text-center d-flex flex-column align-items-center">
            <p className="fs-0 fw-semibold text-800 mb-2">هنوز تاپیکی وجود ندارد</p>
            <p className="fs--1 text-500 mb-0" style={{ maxWidth: '28rem' }}>
              تاپیک‌ها پرامپت‌ها را بر اساس موضوع دسته‌بندی می‌کنند تا بتوانید دیده‌شدن برند
              را در هر حوزه جداگانه ردیابی و مقایسه کنید.
            </p>
            <Button
              size="sm"
              variant="falcon-primary"
              className="mt-3"
              onClick={() => setNewTopicOpen(true)}
            >
              + تاپیک جدید
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div>
          <TopicsTable
            topics={currentData?.rows ?? []}
            total={currentData?.total ?? 0}
            fetchFunction={fetchFunction}
            additionalData={listArgs}
            loading={!currentData}
            expandedTopicId={expandedTopicId}
            onToggleTopic={toggleTopic}
            onDeleteTopic={setPendingDelete}
            onExpandedChange={changeExpandedTopic}
            renderExpandedContent={topic => (
              <TopicPromptsPanel topicId={topic.id} topicName={topic.name} />
            )}
            emptyMessage={
              <div>
                <p className="fw-medium text-500 mb-1">تاپیک مطابقی یافت نشد</p>
                <p className="fs--2 text-400 mb-0">جستجو یا فیلترها را تنظیم کنید.</p>
              </div>
            }
          />
        </div>
      )}

      <CancelModal
        show={pendingDelete != null}
        setShow={() => setPendingDelete(null)}
        title="حذف تاپیک"
        submitTitle="آره، حذف بشه"
        isSubmitting={deleting}
        submitFunc={confirmDelete}
        desc={`آیا مطمئن هستید می‌خواهید تاپیک «${pendingDelete?.name ?? ''}» حذف شود؟ پرامپت‌های آن حذف نمی‌شوند و بدون تاپیک باقی می‌مانند.`}
      />
    </div>
  );
}

export default TopicsPage;
